import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { fmtINR } from "@/lib/format";
import { Play, Loader2, Calculator } from "lucide-react";
import { toast } from "sonner";

const today = () => new Date().toISOString().slice(0, 10);

const ENTITY_LABEL = {
  company: "Company (Pvt. Ltd. / Ltd.)",
  llp: "LLP",
  firm: "Partnership Firm",
  individual_huf: "Individual / HUF (New Regime)",
};

function EditableRow({ label, val, onChange, testid, isSum }) {
  return (
    <tr className={`border-b ${isSum ? "font-semibold bg-muted/30" : ""}`}>
      <td className="py-2 pl-3 text-sm">{label}</td>
      <td className="py-2 pr-3 text-right">
        <Input type="number" step="1" value={val ?? 0} onChange={e => onChange(parseFloat(e.target.value) || 0)}
          className="w-40 text-right font-mono ml-auto h-8" data-testid={testid} />
      </td>
    </tr>
  );
}

export default function AdvanceTax() {
  const [entityType, setEntityType] = useState("company");
  const [isMfg, setIsMfg] = useState(false);
  const [asOf, setAsOf] = useState(today());
  const [overrides, setOverrides] = useState({});
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const compute = async (ov = overrides) => {
    setBusy(true);
    try {
      const r = await api.post("/tax/advance-tax/compute", {
        entity_type: entityType, is_new_manufacturing: isMfg, as_of: asOf, overrides: ov
      });
      setData(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Compute failed"); }
    setBusy(false);
  };
  useEffect(() => { compute({}); setOverrides({}); // eslint-disable-next-line
  }, [entityType, isMfg, asOf]);

  const patch = (k, v) => { const nx = { ...overrides, [k]: v }; setOverrides(nx); };
  const commit = () => compute(overrides);
  const reset = () => { setOverrides({}); compute({}); };

  return (
    <div className="p-6 lg:p-8 space-y-4" data-testid="advance-tax-page">
      <PageHeader eyebrow="Direct Tax · Compliance" title="Advance Tax & MAT Calculator"
        description="Rolling forecast of FY revenue & costs · Sec 211 quarterly schedule · MAT vs Normal Provisions comparison · fully editable." />

      {/* Controls */}
      <div className="card-sharp p-4 flex gap-3 items-end flex-wrap" data-testid="tax-controls">
        <div className="min-w-[240px]"><Label>Entity Type</Label>
          <Select value={entityType} onValueChange={setEntityType}>
            <SelectTrigger className="mt-1" data-testid="entity-type"><SelectValue /></SelectTrigger>
            <SelectContent>{Object.entries(ENTITY_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        {entityType === "company" && (
          <label className="flex items-center gap-2 border border-border px-3 py-2 cursor-pointer" data-testid="new-mfg">
            <Switch checked={isMfg} onCheckedChange={setIsMfg} />
            <span className="text-xs">New Mfg (Sec 115BAB · 15%)</span>
          </label>
        )}
        <div><Label>As of</Label><Input type="date" value={asOf} onChange={e => setAsOf(e.target.value)} className="mt-1" data-testid="as-of" /></div>
        <div className="ml-auto flex gap-2">
          <Button variant="outline" onClick={reset} data-testid="reset-btn">Reset to forecast</Button>
          <Button onClick={commit} disabled={busy} data-testid="recompute-btn">
            {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Computing…</> : <><Calculator className="w-4 h-4 mr-2" /> Recompute</>}
          </Button>
        </div>
      </div>

      {data && (
        <div className="grid lg:grid-cols-2 gap-4">
          {/* Forecast + editable P&L */}
          <div className="card-sharp p-4" data-testid="editable-pnl">
            <div className="text-[10px] font-mono uppercase tracking-widest text-accent mb-3">FY {data.forecast.fy.from} → {data.forecast.fy.to} · Editable projection</div>
            <table className="w-full text-sm">
              <tbody>
                <tr className="border-b bg-muted/30"><td colSpan={2} className="py-1 pl-3 text-[10px] font-mono uppercase text-muted-foreground">YTD (as booked)</td></tr>
                <tr><td className="py-1 pl-3">Revenue YTD</td><td className="text-right pr-3 font-mono">{fmtINR(data.forecast.ytd.revenue)}</td></tr>
                <tr><td className="py-1 pl-3">Expense YTD</td><td className="text-right pr-3 font-mono">{fmtINR(data.forecast.ytd.expense)}</td></tr>
                <tr><td className="py-1 pl-3">Other Income YTD</td><td className="text-right pr-3 font-mono">{fmtINR(data.forecast.ytd.other_income)}</td></tr>
                <tr className="border-b"><td className="py-1 pl-3 text-muted-foreground text-xs">PBT YTD</td><td className="text-right pr-3 font-mono">{fmtINR(data.forecast.ytd.pbt)}</td></tr>

                <tr className="border-b bg-muted/30"><td colSpan={2} className="py-1 pl-3 text-[10px] font-mono uppercase text-muted-foreground">Projected FY (edit to override)</td></tr>
                <EditableRow label="Projected Revenue" val={data.editable.projected_revenue} onChange={v => patch("projected_revenue", v)} testid="proj-rev" />
                <EditableRow label="Projected Other Income" val={data.editable.projected_other_income} onChange={v => patch("projected_other_income", v)} testid="proj-other" />
                <EditableRow label="Projected Expense" val={data.editable.projected_expense} onChange={v => patch("projected_expense", v)} testid="proj-exp" />
                {entityType === "company" && (
                  <EditableRow label="Turnover (for 25%/30% classification)" val={data.editable.company_turnover} onChange={v => patch("company_turnover", v)} testid="turnover" />
                )}
                <EditableRow label="Taxable Income (PBT after adj.)" val={data.editable.taxable_income} onChange={v => patch("taxable_income", v)} testid="taxable-income" isSum />
                <EditableRow label="Less: TDS credited YTD" val={data.editable.tds_credited} onChange={v => patch("tds_credited", v)} testid="tds" />
                <EditableRow label="Less: Advance Tax paid YTD" val={data.editable.advance_tax_paid} onChange={v => patch("advance_tax_paid", v)} testid="adv-paid" />
              </tbody>
            </table>
          </div>

          {/* Tax computation */}
          <div className="space-y-4">
            <div className="card-sharp p-4" data-testid="tax-computation">
              <div className="text-[10px] font-mono uppercase tracking-widest text-accent mb-3">Tax Liability · {data.computation.regime_applied}</div>
              <table className="w-full text-sm">
                <thead><tr className="border-b"><th></th><th className="text-right pr-3">Normal</th>{entityType === "company" && <th className="text-right pr-3">MAT (115JB)</th>}</tr></thead>
                <tbody>
                  <tr className="border-b"><td className="py-1 pl-3 text-muted-foreground text-xs">Rate</td>
                    <td className="text-right pr-3 text-xs">{data.computation.normal.rate_label}</td>
                    {entityType === "company" && <td className="text-right pr-3 text-xs">15% + SC + HEC</td>}
                  </tr>
                  <tr><td className="py-1 pl-3">Basic Tax</td>
                    <td className="text-right pr-3 font-mono">{fmtINR(data.computation.normal.basic)}</td>
                    {entityType === "company" && <td className="text-right pr-3 font-mono">{fmtINR(data.computation.mat.basic)}</td>}
                  </tr>
                  <tr><td className="py-1 pl-3">Surcharge</td>
                    <td className="text-right pr-3 font-mono">{fmtINR(data.computation.normal.surcharge)}</td>
                    {entityType === "company" && <td className="text-right pr-3 font-mono">{fmtINR(data.computation.mat.surcharge)}</td>}
                  </tr>
                  <tr><td className="py-1 pl-3">Health & Edu Cess (4%)</td>
                    <td className="text-right pr-3 font-mono">{fmtINR(data.computation.normal.hec)}</td>
                    {entityType === "company" && <td className="text-right pr-3 font-mono">{fmtINR(data.computation.mat.hec)}</td>}
                  </tr>
                  <tr className="border-t-2 font-semibold">
                    <td className="py-2 pl-3">Total Tax</td>
                    <td className="text-right pr-3 font-mono">{fmtINR(data.computation.normal.total)}</td>
                    {entityType === "company" && <td className="text-right pr-3 font-mono">{fmtINR(data.computation.mat.total)}</td>}
                  </tr>
                  <tr className="bg-primary text-primary-foreground"><td className="py-2 pl-3 font-semibold">Applied ({data.computation.regime_applied})</td><td colSpan={entityType === "company" ? 2 : 1} className="text-right pr-3 font-mono text-lg">{fmtINR(data.computation.total_tax)}</td></tr>
                </tbody>
              </table>
            </div>

            {/* Advance Tax schedule */}
            <div className="card-sharp p-4" data-testid="advance-schedule">
              <div className="text-[10px] font-mono uppercase tracking-widest text-accent mb-3">Advance Tax Schedule · Sec 211</div>
              <div className="text-xs text-muted-foreground mb-2">
                Total tax: <span className="font-mono">{fmtINR(data.advance_tax.total_tax_liability)}</span> ·
                Less TDS: <span className="font-mono">{fmtINR(data.advance_tax.less_tds)}</span> ·
                Less Adv. Paid: <span className="font-mono">{fmtINR(data.advance_tax.less_advance_paid)}</span> ·
                <b> Net payable: <span className="font-mono">{fmtINR(data.advance_tax.net_payable)}</span></b>
              </div>
              <table className="w-full text-sm">
                <thead><tr className="border-b"><th className="text-left">Instalment</th><th className="text-left">Due Date</th><th className="text-right">Cumulative %</th><th className="text-right">This Instalment</th><th className="text-right pr-3">Cumulative</th></tr></thead>
                <tbody>
                  {data.advance_tax.schedule.map(s => (
                    <tr key={s.instalment} className={`border-b ${s.due ? "text-orange-500" : ""}`} data-testid={`inst-${s.instalment}`}>
                      <td className="py-1.5">{s.label}{s.due && <span className="ml-2 text-[9px] font-mono uppercase">DUE</span>}</td>
                      <td className="text-xs">{s.due_date}</td>
                      <td className="text-right font-mono">{s.cumulative_pct}%</td>
                      <td className="text-right font-mono">{fmtINR(s.instalment_amount)}</td>
                      <td className="text-right pr-3 font-mono font-semibold">{fmtINR(s.cumulative_amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
